import React, { useState } from 'react';
import axios from 'axios';
import { colors } from '../theme/tokens';

const RequestPartModal = ({ 
  onClose, 
  onSuccess, 
  initialMake = '', 
  initialModel = '',
  initialVariant = '',
  initialFrom = '',
  initialTo = '',
  showPhotoUpload = false
}) => {
  const [form, setForm] = useState({
    make: initialMake,
    model: initialModel,
    variant: initialVariant,
    from: initialFrom,
    to: initialTo,
    requestedPart: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Photo must be smaller than 5MB');
        return;
      }
      setPhoto(file);
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.make || !form.model || !form.variant || !form.from || !form.to || !form.requestedPart) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      
      // Use FormData for file upload
      const formData = new FormData();
      formData.append('make', form.make);
      formData.append('model', form.model);
      formData.append('variant', form.variant);
      formData.append('yearRange', JSON.stringify({
        from: parseInt(form.from),
        to: parseInt(form.to)
      }));
      formData.append('requestedPart', form.requestedPart);
      
      if (photo) {
        formData.append('photo', photo);
      }

      await axios.post(
        `${API_URL}/api/requests`,
        formData,
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit request');
    } finally {
      setLoading(false);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div style={styles.backdrop} onClick={handleBackdropClick}>
      <div style={styles.modal}>
        <button style={styles.closeBtn} onClick={onClose}>✕</button>
        
        <h2 style={styles.title}>Request Part Data</h2>
        <p style={styles.subtitle}>
          Can't find the car or part you're looking for? Submit a request and our community will help!
        </p>

        {error && <div style={styles.error}>{error}</div>}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Make *</label>
              <input
                name="make"
                value={form.make}
                onChange={handleChange}
                placeholder="e.g., Toyota"
                style={initialMake ? styles.inputReadOnly : styles.input}
                required
                readOnly={!!initialMake}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Model *</label>
              <input
                name="model"
                value={form.model}
                onChange={handleChange}
                placeholder="e.g., Corolla"
                style={initialModel ? styles.inputReadOnly : styles.input}
                required
                readOnly={!!initialModel}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Variant *</label>
            <input
              name="variant"
              value={form.variant}
              onChange={handleChange}
              placeholder="e.g., 1.6 GLi"
              style={initialVariant ? styles.inputReadOnly : styles.input}
              required
              readOnly={!!initialVariant}
            />
          </div>

          <div style={styles.row}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Year From *</label>
              <input
                name="from"
                type="number"
                value={form.from}
                onChange={handleChange}
                placeholder="2010"
                style={initialFrom ? styles.inputReadOnly : styles.input}
                required
                readOnly={!!initialFrom}
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Year To *</label>
              <input
                name="to"
                type="number"
                value={form.to}
                onChange={handleChange}
                placeholder="2015"
                style={initialTo ? styles.inputReadOnly : styles.input}
                required
                readOnly={!!initialTo}
              />
            </div>
          </div>

          <div style={styles.inputGroup}>
            <label style={styles.label}>Part You're Looking For *</label>
            <input
              name="requestedPart"
              value={form.requestedPart}
              onChange={handleChange}
              placeholder="e.g., Door Seal, CV Boot, Radiator Hose"
              style={styles.input}
              required
            />
            <small style={styles.hint}>
              Describe the part you need (e.g., "front door seal", "left CV boot")
            </small>
          </div>

          {showPhotoUpload && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Photo (Optional)</label>
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={styles.fileInput}
              />
              <small style={styles.hint}>
                Upload a photo of the part or area (max 5MB, JPG/PNG/GIF)
              </small>
              {photoPreview && (
                <div style={styles.photoPreview}>
                  <img src={photoPreview} alt="Preview" style={styles.previewImage} />
                  <button 
                    type="button" 
                    onClick={() => { setPhoto(null); setPhotoPreview(null); }}
                    style={styles.removePhotoBtn}
                  >
                    ✕ Remove
                  </button>
                </div>
              )}
            </div>
          )}

          <button type="submit" style={styles.submitBtn} disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Request'}
          </button>
        </form>
      </div>
    </div>
  );
};

const styles = {
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: colors.overlay,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px'
  },
  modal: {
    backgroundColor: colors.surfaceSolid,
    color: colors.text,
    borderRadius: '15px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    padding: '30px',
    position: 'relative',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
    border: `1px solid ${colors.border}`
  },
  closeBtn: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    background: colors.red,
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '35px',
    height: '35px',
    fontSize: '20px',
    cursor: 'pointer',
    fontWeight: 'bold'
  },
  title: {
    color: '#2fb65c',
    marginBottom: '10px',
    fontSize: '24px'
  },
  subtitle: {
    color: colors.textMuted,
    marginBottom: '20px',
    fontSize: '14px'
  },
  error: {
    background: colors.redSoft,
    color: colors.red,
    padding: '10px',
    borderRadius: '5px',
    marginBottom: '15px',
    border: `1px solid ${colors.red}`
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column'
  },
  label: {
    marginBottom: '5px',
    fontWeight: '600',
    color: colors.text,
    fontSize: '14px'
  },
  input: {
    padding: '10px',
    border: `1px solid ${colors.border}`,
    borderRadius: '5px',
    fontSize: '14px',
    background: colors.inputBg,
    color: colors.text
  },
  inputReadOnly: {
    padding: '10px',
    border: `1px solid ${colors.border}`,
    borderRadius: '5px',
    fontSize: '14px',
    background: colors.surfaceSoft,
    color: colors.textMuted,
    cursor: 'not-allowed'
  },
  hint: {
    fontSize: '12px',
    color: colors.textFaint,
    marginTop: '5px'
  },
  fileInput: {
    padding: '10px',
    border: `2px dashed ${colors.border}`,
    borderRadius: '5px',
    fontSize: '14px',
    cursor: 'pointer',
    color: colors.text
  },
  photoPreview: {
    marginTop: '10px',
    border: '2px solid #2fb65c',
    borderRadius: '8px',
    padding: '10px',
    position: 'relative'
  },
  previewImage: {
    width: '100%',
    maxHeight: '200px',
    objectFit: 'contain',
    borderRadius: '5px'
  },
  removePhotoBtn: {
    marginTop: '10px',
    padding: '6px 12px',
    background: colors.red,
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '12px',
    width: '100%'
  },
  submitBtn: {
    padding: '12px 20px',
    background: '#2fb65c',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '10px'
  }
};

export default RequestPartModal;
